<template>
  <div class="min-h-screen bg-white py-24">
    <div class="container mx-auto px-4 max-w-4xl">
      <PageBreadcrumb :paths="[
        { url: '/fr/legal', label: 'Mentions Légales' }
      ]" />
      <h1 class="text-4xl font-light mb-8 text-black">Mentions Légales</h1>
      
      <div class="prose prose-lg max-w-none">
        <h2 class="text-2xl font-light mb-4 text-black">Informations sur l'Entreprise</h2>
        <div class="py-6 rounded-lg mb-8">
          <p class="text-gray-600 mb-2"><strong>Nom de l'entreprise :</strong> {{ company.name }}</p>
          <p class="text-gray-600 mb-2"><strong>Adresse enregistrée :</strong> {{ company.address }}</p>
          <p class="text-gray-600 mb-2"><strong>Numéro d'enregistrement :</strong> {{ company.registrationNumber }}</p>
          <p class="text-gray-600 mb-2"><strong>Forme juridique :</strong> société par actions simplifiée unipersonnelle (SASU) au capital de 1 000 €</p>
          <p class="text-gray-600 mb-2"><strong>Directeur de la publication :</strong> Kendrick Besnard, Président</p>
          <p class="text-gray-600"><strong>Email :</strong> <a href="/fr/contact" class="text-black underline">Nous contacter</a></p>
        </div>

        <h2 class="text-2xl font-light mb-4 text-black">Informations sur le Site Web</h2>
        <p class="text-gray-600 mb-6">
          Ce site web est exploité par {{ company.name }}, une entreprise française spécialisée dans la conception et la fabrication de chaussures de luxe.
        </p>

        <h3 class="text-xl font-medium mb-3 text-black">Propriété Intellectuelle</h3>
        <p class="text-gray-600 mb-6">
          Tout le contenu de ce site web, y compris mais sans s'y limiter, le texte, les graphiques, les logos, les images et les logiciels, est la propriété de {{ company.name }} et est protégé par les lois françaises et internationales sur le droit d'auteur.
        </p>

        <h3 class="text-xl font-medium mb-3 text-black">Marques de Commerce</h3>
        <p class="text-gray-600 mb-6">
          Le nom {{ company.name }}, le logo et toutes les marques de commerce associées sont la propriété exclusive de {{ company.name }} et ne peuvent pas être utilisés sans autorisation écrite préalable.
        </p>

        <h3 class="text-xl font-medium mb-3 text-black">Règles d'Utilisation du Site Web</h3>
        <ul class="list-disc pl-6 mb-6 text-gray-600 space-y-2">
          <li>Les utilisateurs ne peuvent pas utiliser le site web à des fins illégales ou de manière à endommager, désactiver ou altérer le site web.</li>
          <li>Les utilisateurs ne peuvent pas tenter d'accéder sans autorisation à une partie du site web ou à d'autres systèmes ou réseaux connectés au site web.</li>
          <li>Les utilisateurs ne peuvent pas utiliser de moyens automatisés pour accéder au site web à quelque fin que ce soit sans notre autorisation écrite expresse.</li>
          <li>Les utilisateurs doivent respecter les droits de propriété intellectuelle de {{ company.name }} et des tiers.</li>
        </ul>

        <h3 class="text-xl font-medium mb-3 text-black">Confidentialité et Protection des Données</h3>
        <p class="text-gray-600 mb-6">
          Nous nous engageons à protéger votre vie privée et vos données personnelles. Pour des informations détaillées sur la façon dont nous collectons, utilisons et protégeons vos informations, veuillez consulter notre 
          <NuxtLink to="/fr/privacy" class="text-black underline">Politique de Confidentialité</NuxtLink> 
          .
        </p>

        <h3 class="text-xl font-medium mb-3 text-black">Limitation de Responsabilité</h3>
        <p class="text-gray-600 mb-6">
          {{ company.name }} ne saurait être tenu responsable des dommages directs, indirects, accessoires, spéciaux ou consécutifs résultant de l'utilisation ou de l'impossibilité d'utiliser ce site web ou son contenu.
        </p>

        <h3 class="text-xl font-medium mb-3 text-black">Droit Applicable</h3>
        <p class="text-gray-600 mb-6">
          Ces mentions légales sont régies par le droit français. Tout litige découlant de l'utilisation de ce site web sera soumis à la juridiction exclusive des tribunaux français.
        </p>

        <h3 class="text-xl font-medium mb-3 text-black">Mises à Jour des Mentions Légales</h3>
        <p class="text-gray-600 mb-6">
          Nous nous réservons le droit de mettre à jour ces mentions légales à tout moment. Les utilisateurs sont invités à consulter ces mentions périodiquement pour tout changement.
        </p>

        <h3 class="text-xl font-medium mb-3 text-black">Informations de Contact</h3>
        <p class="text-gray-600">
          Pour toute question concernant ces mentions légales, veuillez 
          <a href="/fr/contact" class="text-black underline">nous contacter</a>
          .
        </p>
        <ul class="list-none pl-0 mt-4 text-gray-600 space-y-2">
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useSupabaseClient } from '~/composables/useSupabaseClient'

const config = useRuntimeConfig()
const supabase = useSupabaseClient()
const dbCompanyInfo = ref<any>(null)
const loading = ref(true)

// Fetch company info from database first, fall back to runtime config
const company = computed(() => {
  if (dbCompanyInfo.value) {
    return {
      name: dbCompanyInfo.value.company_name || config.public.company.name,
      address: dbCompanyInfo.value.company_address || config.public.company.address,
      registrationNumber: dbCompanyInfo.value.registration_number || config.public.company.registrationNumber
    }
  }
  return config.public.company as { name: string; address: string; registrationNumber: string }
})

const fetchCompanyInfo = async () => {
  try {
    const { data, error } = await supabase
      .from('company_info')
      .select('*')
      .eq('is_active', true)
      .limit(1)
      .single()

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching company info:', error)
      return
    }

    if (data) {
      dbCompanyInfo.value = data
    }
  } catch (err) {
    console.error('Error fetching company info:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchCompanyInfo()
})

useHead({
  title: 'Mentions Légales - Kendrick',
  meta: [
    { name: 'description', content: 'Mentions légales et informations sur l\'entreprise pour Kendrick.' }
  ]
})
</script>

<style scoped>
/* editorial — utility-only page */
</style>
